/**
 * Asset Reports Page
 * Comprehensive fixed assets reporting
 */

import React, { useState, useEffect } from 'react'
import { FileText, Download, Printer, Calendar, TrendingDown, Package } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AdminUser } from '@/lib/authService'
import { formatCurrency } from '@/lib/currency'
import AssetsService, { AssetSummary } from '@/services/assetsService'
import DepreciationService from '@/services/depreciationService'
import { supabase } from '@/lib/supabase'

interface AssetReportsProps {
  user: AdminUser | null
}

export default function AssetReports({ user }: AssetReportsProps) {
  const [loading, setLoading] = useState(false)
  const [assets, setAssets] = useState<AssetSummary[]>([])
  const [depreciationHistory, setDepreciationHistory] = useState<any[]>([])
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString())
  const [selectedCategory, setSelectedCategory] = useState('all')

  useEffect(() => {
    if (user?.churchId) {
      loadReportData()
    }
  }, [user, selectedYear, selectedCategory])

  async function loadReportData() {
    if (!user?.churchId) return

    setLoading(true)
    try {
      const filters: any = {}
      if (selectedCategory !== 'all') {
        filters.category = selectedCategory
      }

      const assetsData = await AssetsService.getAssets(user.churchId, filters)
      setAssets(assetsData)

      // Load depreciation history for the year
      const { data: historyData } = await supabase
        .from('asset_depreciation_history')
        .select(`
          *,
          asset:assets!inner(
            asset_code,
            asset_name,
            asset_category
          )
        `)
        .eq('church_id', user.churchId)
        .eq('fiscal_year', parseInt(selectedYear))
        .order('depreciation_date', { ascending: false })

      setDepreciationHistory(historyData || [])
    } catch (error) {
      console.error('Error loading report data:', error)
    } finally {
      setLoading(false)
    }
  }

  function handlePrintReport(reportType: string) {
    window.print()
  }

  function handleExportCSV(reportType: string) {
    let csvContent = ''
    let filename = ''

    if (reportType === 'register') {
      filename = `asset-register-${new Date().toISOString().split('T')[0]}.csv`
      csvContent = 'Asset Code,Asset Name,Category,Purchase Date,Purchase Cost,Salvage Value,Accumulated Depreciation,Current Value,Status\n'
      assets.forEach((asset) => {
        csvContent += `"${asset.asset_code}","${asset.asset_name}","${asset.asset_category}","${asset.purchase_date}",${asset.purchase_cost},${asset.salvage_value},${asset.accumulated_depreciation},${asset.current_value},"${asset.status}"\n`
      })
    } else if (reportType === 'depreciation') {
      filename = `depreciation-schedule-${selectedYear}.csv`
      csvContent = 'Date,Asset Code,Asset Name,Opening Value,Depreciation,Accumulated Depreciation,Closing Value\n'
      depreciationHistory.forEach((item) => {
        const openingValue = (item.book_value_after || 0) + (item.depreciation_amount || 0)
        csvContent += `"${item.depreciation_date}","${item.asset?.asset_code}","${item.asset?.asset_name}",${openingValue},${item.depreciation_amount},${item.accumulated_depreciation_after},${item.book_value_after}\n`
      })
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename
    link.click()
  }

  const totalCost = assets.reduce((sum, a) => sum + a.purchase_cost, 0)
  const totalDepreciation = assets.reduce((sum, a) => sum + a.accumulated_depreciation, 0)
  const totalCurrentValue = assets.reduce((sum, a) => sum + a.current_value, 0)

  // Group assets by category
  const assetsByCategory = assets.reduce((acc, asset) => {
    const category = asset.asset_category
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(asset)
    return acc
  }, {} as Record<string, AssetSummary[]>)

  // Calculate category summaries
  const categorySummaries = Object.entries(assetsByCategory).map(([category, categoryAssets]) => ({
    category,
    count: categoryAssets.length,
    purchaseCost: categoryAssets.reduce((sum, a) => sum + a.purchase_cost, 0),
    accumulatedDepreciation: categoryAssets.reduce((sum, a) => sum + a.accumulated_depreciation, 0),
    currentValue: categoryAssets.reduce((sum, a) => sum + a.current_value, 0),
  }))

  // Group depreciation history by month
  const depreciationByMonth = depreciationHistory.reduce((acc, item) => {
    const month = item.fiscal_period
    if (!acc[month]) {
      acc[month] = {
        month,
        totalDepreciation: 0,
        count: 0,
      }
    }
    acc[month].totalDepreciation += item.depreciation_amount
    acc[month].count++
    return acc
  }, {} as Record<number, any>)

  const monthlyDepreciation = Object.values(depreciationByMonth).sort((a: any, b: any) => a.month - b.month)

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Fixed Assets Reports</h1>
            <p className="text-sm text-muted-foreground">
              Comprehensive asset reports and depreciation schedules
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Report Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1 max-w-xs space-y-2">
                <Label htmlFor="year">Fiscal Year</Label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2024">2024</SelectItem>
                    <SelectItem value="2023">2023</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1 max-w-xs space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="land">Land</SelectItem>
                    <SelectItem value="building">Building</SelectItem>
                    <SelectItem value="vehicle">Vehicle</SelectItem>
                    <SelectItem value="furniture">Furniture</SelectItem>
                    <SelectItem value="equipment">Equipment</SelectItem>
                    <SelectItem value="computer">Computer</SelectItem>
                    <SelectItem value="musical_instrument">Musical Instrument</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="register" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="register">Asset Register</TabsTrigger>
            <TabsTrigger value="depreciation">Depreciation Schedule</TabsTrigger>
            <TabsTrigger value="summary">Summary by Category</TabsTrigger>
            <TabsTrigger value="monthly">Monthly Depreciation</TabsTrigger>
          </TabsList>

          {/* Asset Register Report */}
          <TabsContent value="register">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Asset Register</CardTitle>
                    <CardDescription>Complete list of all fixed assets</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handlePrintReport('register')}>
                      <Printer className="mr-2 h-4 w-4" />
                      Print
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExportCSV('register')}>
                      <Download className="mr-2 h-4 w-4" />
                      Export CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Code</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Purchase Date</TableHead>
                          <TableHead className="text-right">Purchase Cost</TableHead>
                          <TableHead className="text-right">Accumulated Dep.</TableHead>
                          <TableHead className="text-right">Current Value</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {assets.map((asset) => (
                          <TableRow key={asset.id}>
                            <TableCell className="font-medium">{asset.asset_code}</TableCell>
                            <TableCell>{asset.asset_name}</TableCell>
                            <TableCell className="capitalize">{asset.asset_category.replace('_', ' ')}</TableCell>
                            <TableCell>{new Date(asset.purchase_date).toLocaleDateString()}</TableCell>
                            <TableCell className="text-right">{formatCurrency(asset.purchase_cost, 'GHS')}</TableCell>
                            <TableCell className="text-right">{formatCurrency(asset.accumulated_depreciation, 'GHS')}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(asset.current_value, 'GHS')}</TableCell>
                            <TableCell className="capitalize">{asset.status}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <div className="mt-6 border-t pt-4">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-muted-foreground">Total Assets</div>
                          <div className="text-xl font-bold">{assets.length}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Total Purchase Cost</div>
                          <div className="text-xl font-bold">{formatCurrency(totalCost, 'GHS')}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Total Current Value</div>
                          <div className="text-xl font-bold">{formatCurrency(totalCurrentValue, 'GHS')}</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Depreciation Schedule */}
          <TabsContent value="depreciation">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Depreciation Schedule - {selectedYear}</CardTitle>
                    <CardDescription>Monthly depreciation postings</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handlePrintReport('depreciation')}>
                      <Printer className="mr-2 h-4 w-4" />
                      Print
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExportCSV('depreciation')}>
                      <Download className="mr-2 h-4 w-4" />
                      Export CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Asset Code</TableHead>
                      <TableHead>Asset Name</TableHead>
                      <TableHead className="text-right">Opening Value</TableHead>
                      <TableHead className="text-right">Depreciation</TableHead>
                      <TableHead className="text-right">Accum. Depreciation</TableHead>
                      <TableHead className="text-right">Closing Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {depreciationHistory.map((item) => {
                      const openingValue = (item.book_value_after || 0) + (item.depreciation_amount || 0)
                      return (
                        <TableRow key={item.id}>
                          <TableCell>{new Date(item.depreciation_date).toLocaleDateString()}</TableCell>
                          <TableCell className="font-medium">{item.asset?.asset_code}</TableCell>
                          <TableCell>{item.asset?.asset_name}</TableCell>
                          <TableCell className="text-right">{formatCurrency(openingValue, 'GHS')}</TableCell>
                          <TableCell className="text-right text-red-600">{formatCurrency(item.depreciation_amount, 'GHS')}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.accumulated_depreciation_after, 'GHS')}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(item.book_value_after, 'GHS')}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Summary by Category */}
          <TabsContent value="summary">
            <Card>
              <CardHeader>
                <CardTitle>Summary by Asset Category</CardTitle>
                <CardDescription>Asset values grouped by category</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Count</TableHead>
                      <TableHead className="text-right">Purchase Cost</TableHead>
                      <TableHead className="text-right">Accumulated Dep.</TableHead>
                      <TableHead className="text-right">Current Value</TableHead>
                      <TableHead className="text-right">% of Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categorySummaries.map((summary) => (
                      <TableRow key={summary.category}>
                        <TableCell className="font-medium capitalize">{summary.category.replace('_', ' ')}</TableCell>
                        <TableCell className="text-right">{summary.count}</TableCell>
                        <TableCell className="text-right">{formatCurrency(summary.purchaseCost, 'GHS')}</TableCell>
                        <TableCell className="text-right">{formatCurrency(summary.accumulatedDepreciation, 'GHS')}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(summary.currentValue, 'GHS')}</TableCell>
                        <TableCell className="text-right">{((summary.currentValue / totalCurrentValue) * 100).toFixed(1)}%</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-semibold bg-muted/50">
                      <TableCell>TOTAL</TableCell>
                      <TableCell className="text-right">{assets.length}</TableCell>
                      <TableCell className="text-right">{formatCurrency(totalCost, 'GHS')}</TableCell>
                      <TableCell className="text-right">{formatCurrency(totalDepreciation, 'GHS')}</TableCell>
                      <TableCell className="text-right">{formatCurrency(totalCurrentValue, 'GHS')}</TableCell>
                      <TableCell className="text-right">100%</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monthly Depreciation */}
          <TabsContent value="monthly">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Depreciation - {selectedYear}</CardTitle>
                <CardDescription>Depreciation expense by month</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Assets Depreciated</TableHead>
                      <TableHead className="text-right">Total Depreciation</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthlyDepreciation.map((item: any) => {
                      const monthName = new Date(parseInt(selectedYear), item.month - 1, 1).toLocaleDateString('en-US', { month: 'long' })
                      return (
                        <TableRow key={item.month}>
                          <TableCell className="font-medium">{monthName}</TableCell>
                          <TableCell className="text-right">{item.count}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.totalDepreciation, 'GHS')}</TableCell>
                        </TableRow>
                      )
                    })}
                    <TableRow className="font-semibold bg-muted/50">
                      <TableCell>TOTAL</TableCell>
                      <TableCell className="text-right">{depreciationHistory.length}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(depreciationHistory.reduce((sum, item) => sum + item.depreciation_amount, 0), 'GHS')}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
